import api from "./apiClient";

function unwrap<T>(raw: { data: { data: T } }): T {
  return raw.data.data;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PlatformStats {
  totalUsers: number;
  totalSchools: number;
  totalStudents: number;
  activeSchools: number;
  activeStudents: number;
  newUsersThisMonth: number;
  usersByType: Record<string, number>;
  schoolsByTier: Record<string, number>;
}

export interface EngagementMetrics {
  period: string;
  newUsers: number;
  newStudents: number;
  newAssessments: number;
  achievementsUnlocked: number;
}

export interface AdminSchool {
  _id: string;
  name: string;
  code: string;
  email: string;
  phone: string;
  status: "active" | "inactive" | "pending" | "suspended";
  tier: "basic" | "standard" | "premium" | "enterprise";
  type: string;
  address: { street: string; city: string; state: string; country: string };
  principalName: string;
  principalEmail: string;
  principalPhone: string;
  adminId: { _id: string; name: string; email: string } | null;
  statistics: { totalStudents: number; totalTeachers: number; totalStaff: number };
  subscription: { plan: string; startDate: string; endDate: string; isActive: boolean; billingEmail: string };
  branches: Array<{ id: string; name: string; isActive: boolean; phone?: string; principalName?: string }>;
  createdAt: string;
}

export interface AdminUser {
  _id: string;
  name: string;
  email: string;
  userType: "student" | "school_admin" | "super_admin" | "parent";
  role: string;
  status: "active" | "inactive" | "suspended" | "pending";
  emailVerified: boolean;
  loginAttempts: number;
  lastLogin?: string;
  createdAt: string;
  studentProfile?: string;
  schoolProfile?: string;
}

export interface AuditLogEntry {
  _id: string;
  actorId: { _id: string; name: string; email: string; userType: string } | string;
  actorType: string;
  action: string;
  targetType?: string;
  targetId?: string;
  before?: unknown;
  after?: unknown;
  ipAddress?: string;
  createdAt: string;
}

export interface ListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  tier?: string;
  userType?: string;
  since?: string;
}

// ── API ───────────────────────────────────────────────────────────────────────

export const superAdminApi = {
  // Stats
  getStats: async (): Promise<PlatformStats> =>
    unwrap(await api.get("/admin/stats")),

  getEngagement: async (days = 30): Promise<EngagementMetrics> =>
    unwrap(await api.get("/admin/engagement", { params: { days } })),

  // Schools
  listSchools: async (params: ListParams = {}) =>
    unwrap<{ schools: AdminSchool[]; total: number; page: number; pages: number }>(
      await api.get("/admin/schools", { params })
    ),

  getSchool: async (id: string): Promise<{ school: AdminSchool }> =>
    unwrap(await api.get(`/admin/schools/${id}`)),

  createSchool: async (data: Record<string, unknown>): Promise<{ school: AdminSchool }> =>
    unwrap(await api.post("/admin/schools", data)),

  updateSchool: async (id: string, data: Record<string, unknown>): Promise<{ school: AdminSchool }> =>
    unwrap(await api.patch(`/admin/schools/${id}`, data)),

  updateSchoolStatus: async (id: string, status: string): Promise<{ school: AdminSchool }> =>
    unwrap(await api.patch(`/admin/schools/${id}/status`, { status })),

  updateSubscription: async (
    id: string,
    data: { plan?: string; endDate?: string; isActive?: boolean; billingEmail?: string }
  ): Promise<{ school: AdminSchool }> =>
    unwrap(await api.patch(`/admin/schools/${id}/subscription`, data)),

  deleteSchool: async (id: string): Promise<{ message: string }> =>
    unwrap(await api.delete(`/admin/schools/${id}`)),

  // Users
  listUsers: async (params: ListParams = {}) =>
    unwrap<{ users: AdminUser[]; total: number; page: number; pages: number }>(
      await api.get("/admin/users", { params })
    ),

  getUser: async (id: string): Promise<{ user: AdminUser; profile: unknown }> =>
    unwrap(await api.get(`/admin/users/${id}`)),

  suspendUser: async (id: string) =>
    unwrap(await api.patch(`/admin/users/${id}/suspend`, {})),

  activateUser: async (id: string) =>
    unwrap(await api.patch(`/admin/users/${id}/activate`, {})),

  deleteUser: async (id: string) =>
    unwrap(await api.delete(`/admin/users/${id}`)),

  // Audit log
  getAuditLogs: async (params: ListParams = {}) =>
    unwrap<{ logs: AuditLogEntry[]; total: number; page: number; pages: number }>(
      await api.get("/admin/audit-logs", { params })
    ),
};
